// @ts-ignore - vendored self-contained Three.js module.
import * as THREE from '../../vendor/three.module.min.js';

interface TrailPuff {
  position: any;
  life: number;
  maxLife: number;
  baseScale: number;
  color: number;
}

interface TrackMark {
  x: number;
  z: number;
  /** Tank heading in the same atan2(dx, -dz) convention as the chassis. */
  angle: number;
  life: number;
  maxLife: number;
  width: number;
}

/**
 * Allocation-free projectile trails and temporary tread marks backed by two InstancedMeshes.
 * Projectile puffs use additive fading; paired tread marks use one low-opacity material
 * and shrink near the end of their lifetime without per-instance color uploads.
 */
export class TrailSystem {
  private readonly puffs: TrailPuff[] = [];
  private readonly puffPool: TrailPuff[] = [];
  private readonly marks: TrackMark[] = [];
  private readonly markPool: TrackMark[] = [];

  private readonly puffGeometry = new THREE.SphereGeometry(0.16, 8, 6);
  private readonly markGeometry = new THREE.PlaneGeometry(0.12, 0.34);

  private readonly puffInstances: any;
  private readonly markInstances: any;
  private readonly puffMaterial: any;
  private readonly markMaterial: any;
  private readonly dummy = new THREE.Object3D();
  private readonly tempColor = new THREE.Color();

  private readonly maxPuffs: number;
  private readonly maxMarks: number;
  private qualityScale: number;

  constructor(private readonly parent: any, qualityScale = 1) {
    this.qualityScale = THREE.MathUtils.clamp(qualityScale, 0.35, 1);
    this.maxPuffs = Math.max(48, Math.round(192 * this.qualityScale));
    this.maxMarks = Math.max(24, Math.round(110 * this.qualityScale));

    // Preallocate state pools to avoid first-shot and first-movement GC spikes.
    for (let i = 0; i < this.maxPuffs; i += 1) this.puffPool.push(this.createPuffState());
    for (let i = 0; i < this.maxMarks; i += 1) this.markPool.push(this.createMarkState());

    this.puffMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      vertexColors: true,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });
    // Low-opacity normal blending stays subtle on bright ground and avoids multiply-black overlaps.
    this.markMaterial = new THREE.MeshBasicMaterial({
      color: 0x355047,
      transparent: true,
      opacity: 0.2,
      depthWrite: false,
      blending: THREE.NormalBlending,
      toneMapped: false,
    });

    this.puffInstances = this.createSurface(this.puffGeometry, this.puffMaterial, this.maxPuffs);
    this.markInstances = this.createSurface(this.markGeometry, this.markMaterial, this.maxMarks * 2, false);
    this.markInstances.renderOrder = 1;
    this.parent.add(this.puffInstances, this.markInstances);
  }

  private createSurface(geometry: any, material: any, capacity: number, usesInstanceColor = true): any {
    const mesh = new THREE.InstancedMesh(geometry, material, capacity);
    // Compile the shader with one zero-scale instance; the first update resets count.
    mesh.count = capacity > 0 ? 1 : 0;
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    if (capacity > 0) {
      mesh.setMatrixAt(0, new THREE.Matrix4().makeScale(0, 0, 0));
      if (usesInstanceColor) {
        mesh.setColorAt(0, this.tempColor.setHex(0xffffff));
        mesh.instanceColor?.setUsage(THREE.DynamicDrawUsage);
      }
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
    return mesh;
  }

  setQualityScale(value: number): void {
    this.qualityScale = THREE.MathUtils.clamp(value, 0.35, 1);
  }

  /** Emit one projectile puff at a world-space position. */
  emitTrail(x: number, y: number, z: number, color: number, size = 1): void {
    // Quality changes apply immediately by tightening the live-state cap.
    if (this.puffs.length >= Math.max(24, Math.round(this.maxPuffs * this.qualityScale))) this.releasePuff(0);
    const puff = this.puffPool.pop() ?? this.createPuffState();
    puff.position.set(x, y, z);
    puff.life = 0.24;
    puff.maxLife = 0.24;
    puff.baseScale = size * (0.75 + Math.random() * 0.4);
    puff.color = color;
    this.puffs.push(puff);
  }

  /** Stamp a paired tread state at the rear-center world position of a tank. */
  stampTrack(x: number, z: number, angle: number, width = 1): void {
    if (this.marks.length >= Math.max(12, Math.round(this.maxMarks * this.qualityScale))) this.releaseMark(0);
    const mark = this.markPool.pop() ?? this.createMarkState();
    mark.x = x;
    mark.z = z;
    mark.angle = angle;
    mark.life = 2.6;
    mark.maxLife = 2.6;
    mark.width = width;
    this.marks.push(mark);
  }

  update(delta: number): void {
    for (let i = this.puffs.length - 1; i >= 0; i -= 1) {
      this.puffs[i].life -= delta;
      if (this.puffs[i].life <= 0) this.releasePuff(i);
    }
    for (let i = this.marks.length - 1; i >= 0; i -= 1) {
      this.marks[i].life -= delta;
      if (this.marks[i].life <= 0) this.releaseMark(i);
    }
    this.syncInstances();
  }

  clear(): void {
    while (this.puffs.length > 0) this.releasePuff(this.puffs.length - 1);
    while (this.marks.length > 0) this.releaseMark(this.marks.length - 1);
    this.syncInstances();
  }

  /** Release all owned GPU resources during world teardown. */
  dispose(): void {
    this.parent.remove(this.puffInstances, this.markInstances);
    this.puffInstances.dispose?.();
    this.markInstances.dispose?.();
    this.puffGeometry.dispose();
    this.markGeometry.dispose();
    this.puffMaterial.dispose();
    this.markMaterial.dispose();
    this.puffs.length = 0;
    this.puffPool.length = 0;
    this.marks.length = 0;
    this.markPool.length = 0;
  }

  get activeCount(): number {
    return this.puffs.length + this.marks.length;
  }

  get diagnostics(): Record<string, number> {
    return {
      puffs: this.puffs.length,
      marks: this.marks.length,
      maxPuffs: this.maxPuffs,
      drawSurfaces: 2,
    };
  }

  private createPuffState(): TrailPuff {
    return {
      position: new THREE.Vector3(),
      life: 0,
      maxLife: 0,
      baseScale: 1,
      color: 0xffffff,
    };
  }

  private releasePuff(index: number): void {
    const puff = this.puffs[index];
    const last = this.puffs.pop();
    if (!puff || !last) return;
    if (index < this.puffs.length) this.puffs[index] = last;
    this.puffPool.push(puff);
  }

  private createMarkState(): TrackMark {
    return { x: 0, z: 0, angle: 0, life: 0, maxLife: 0, width: 1 };
  }

  private releaseMark(index: number): void {
    const mark = this.marks[index];
    const last = this.marks.pop();
    if (!mark || !last) return;
    if (index < this.marks.length) this.marks[index] = last;
    this.markPool.push(mark);
  }

  private syncInstances(): void {
    let puffCount = 0;
    for (const puff of this.puffs) {
      const normalized = Math.max(0, puff.life / puff.maxLife);
      const progress = 1 - normalized;
      // Expand quickly, then shrink with additive fading to avoid a hard disappearance.
      const scale = puff.baseScale * (0.55 + progress * 1.1) * Math.min(1, normalized * 4.5);
      this.dummy.position.copy(puff.position);
      this.dummy.rotation.set(0, 0, 0);
      this.dummy.scale.setScalar(Math.max(0.001, scale));
      this.dummy.updateMatrix();
      this.puffInstances.setMatrixAt(puffCount, this.dummy.matrix);
      // Additive particles fade toward black; brighten the head of each trail.
      this.tempColor.setHex(puff.color);
      this.tempColor.multiplyScalar((0.35 + normalized * 0.75) * (normalized > 0.82 ? 1.35 : 1));
      this.puffInstances.setColorAt(puffCount, this.tempColor);
      puffCount += 1;
    }
    this.commitInstances(this.puffInstances, puffCount);

    let markCount = 0;
    for (const mark of this.marks) {
      const normalized = Math.max(0, mark.life / mark.maxLife);
      const age = mark.maxLife - mark.life;
      // Roll the tread in quickly during its first 0.12 seconds.
      const rollOut = 0.7 + 0.3 * Math.min(1, age * 8);
      const fadeOut = Math.min(1, normalized * 5);
      const scale = rollOut * fadeOut;
      const rightX = Math.cos(mark.angle);
      const rightZ = Math.sin(mark.angle);
      for (let side = -1; side <= 1; side += 2) {
        this.dummy.position.set(mark.x + rightX * side * 0.78, 0.045, mark.z + rightZ * side * 0.78);
        // Align in the plane first, then lay the mark flat on the ground.
        this.dummy.rotation.set(-Math.PI / 2, 0, -mark.angle);
        this.dummy.scale.set(mark.width * scale, scale, 1);
        this.dummy.updateMatrix();
        this.markInstances.setMatrixAt(markCount, this.dummy.matrix);
        markCount += 1;
      }
    }
    this.commitInstances(this.markInstances, markCount);
  }

  private commitInstances(mesh: any, count: number): void {
    const countChanged = mesh.count !== count;
    mesh.count = count;
    if (count > 0 || countChanged) {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  }
}
